import React, { useState } from "react";
import { formatDateTime } from "../../lib/date";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../../api/client";
import { useAuth } from "../../contexts/AuthContext";
import type { User, Directorate } from "../../types";
import { ROLE_LABELS } from "../../types";

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const qc = useQueryClient();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ username: "", fullName: "", password: "", role: "VIEWER", directorateId: "" });
  const [resetId, setResetId] = useState<number | null>(null);
  const [resetValue, setResetValue] = useState("");

  const { data } = useQuery({
    queryKey: ["users-list"],
    queryFn:  () => api.get<{ data: User[] }>("/users").then(r => r.data.data),
  });

  const { data: dirs } = useQuery({
    queryKey: ["directorates"],
    queryFn:  () => api.get<{ data: Directorate[] }>("/directorates?isActive=true").then(r => r.data.data),
  });

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(""); setSuccess("");
    if ((form.role === "DATA_ENTRY" || form.role === "VIEWER") && !form.directorateId) {
      setError("يجب ربط مديرية لدور إدخال البيانات أو المشاهدة");
      return;
    }
    try {
      await api.post("/users", {
        ...form,
        directorateId: form.directorateId ? parseInt(form.directorateId) : null,
      });
      qc.invalidateQueries({ queryKey: ["users-list"] });
      setSuccess("تم إنشاء المستخدم بنجاح");
      setShowForm(false);
      setForm({ username: "", fullName: "", password: "", role: "VIEWER", directorateId: "" });
    } catch (err: unknown) {
      setError((err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? "حدث خطأ");
    }
  };

  const toggleActive = async (id: number, current: boolean) => {
    if (currentUser?.id === id && current) {
      setError("لا يمكنك تعطيل حسابك الحالي");
      return;
    }
    try {
      await api.put(`/users/${id}`, { isActive: !current });
      qc.invalidateQueries({ queryKey: ["users-list"] });
    } catch {}
  };

  const submitReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (resetId == null) return;
    setError(""); setSuccess("");
    try {
      await api.put(`/users/${resetId}/password`, { newPassword: resetValue });
      setSuccess("تم إعادة تعيين كلمة المرور — سيُطلب من المستخدم تغييرها عند الدخول");
      setResetId(null); setResetValue("");
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? "حدث خطأ"
      );
    }
  };

  const deleteUser = async (id: number, username: string) => {
    if (!confirm(`حذف "${username}" نهائياً من قاعدة البيانات؟ هذا لا يمكن التراجع عنه.`)) return;
    setError(""); setSuccess("");
    try {
      await api.delete(`/users/${id}`);
      qc.invalidateQueries({ queryKey: ["users-list"] });
      setSuccess("تم حذف المستخدم");
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? "حدث خطأ"
      );
    }
  };

  const users = data ?? [];

  return (
    <div>
      <div className="page-header flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="page-title">إدارة المستخدمين</h1>
          <p className="page-subtitle">الأدوار والصلاحيات</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary">
          {showForm ? "إلغاء" : "+ مستخدم جديد"}
        </button>
      </div>

      {error   && <div className="alert-error   mb-4">{error}</div>}
      {success && <div className="alert-success mb-4">{success}</div>}

      {showForm && (
        <form onSubmit={handleCreate} className="card mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label">اسم المستخدم</label>
            <input className="input" value={form.username} onChange={e => setForm(f => ({...f, username: e.target.value}))} required />
          </div>
          <div>
            <label className="label">الاسم الكامل</label>
            <input className="input" value={form.fullName} onChange={e => setForm(f => ({...f, fullName: e.target.value}))} required />
          </div>
          <div>
            <label className="label">كلمة المرور</label>
            <input type="password" className="input" value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} required />
          </div>
          <div>
            <label className="label">الدور</label>
            <select className="input" value={form.role} onChange={e => setForm(f => ({...f, role: e.target.value}))}>
              {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="label">المديرية (اختياري)</label>
            <select className="input" value={form.directorateId} onChange={e => setForm(f => ({...f, directorateId: e.target.value}))}>
              <option value="">كل المديريات</option>
              {(dirs ?? []).map(d => <option key={d.id} value={d.id}>{d.nameAr} — {d.governorate?.nameAr}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <button type="submit" className="btn-primary">إنشاء المستخدم</button>
          </div>
        </form>
      )}

      <div className="table-container">
        <table className="table">
          <thead>
            <tr><th>الاسم الكامل</th><th>اسم المستخدم</th><th>الدور</th><th>المديرية</th><th>آخر دخول</th><th>الحالة</th><th>إجراء</th></tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td className="font-medium">{u.fullName}</td>
                <td><code className="text-xs bg-gray-100 px-1 rounded">{u.username}</code></td>
                <td><span className="text-xs bg-primary-50 text-primary-700 px-2 py-0.5 rounded-full">{ROLE_LABELS[u.role]}</span></td>
                <td className="text-xs text-sand-500">{u.directorateId ? (() => { const d = (dirs ?? []).find(x => x.id === u.directorateId); return d ? `${d.nameAr} — ${d.governorate?.nameAr ?? ""}` : `#${u.directorateId}`; })() : "كل المديريات"}</td>
                <td className="text-xs text-gray-400"><span dir="ltr">{u.lastLoginAt ? formatDateTime(u.lastLoginAt) : "—"}</span></td>
                <td>
                  <span className={u.isActive ? "text-green-600" : "text-red-500"}>{u.isActive ? "فعّال" : "معطّل"}</span>
                  {u.isProtected && <span className="text-xs text-gold-700 mr-1" title="حساب محمي">🔒</span>}
                </td>
                <td className="whitespace-nowrap">
                  <button onClick={() => toggleActive(u.id, u.isActive)} className="text-xs text-primary-600 hover:underline ml-3">
                    {u.isActive ? "تعطيل" : "تفعيل"}
                  </button>
                  {!u.isProtected && (
                    <button onClick={() => { setResetId(u.id); setResetValue(""); setError(""); setSuccess(""); }}
                      className="text-xs text-primary-600 hover:underline ml-3">
                      إعادة تعيين كلمة المرور
                    </button>
                  )}
                  {currentUser?.id !== u.id && (
                    <button onClick={() => deleteUser(u.id, u.username)} className="text-xs text-[#6b1f2a] hover:underline">
                      حذف
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {resetId != null && (
              <tr><td colSpan={7}>
                <form onSubmit={submitReset} className="p-3 flex flex-wrap items-end gap-3">
                  <div>
                    <label className="label">كلمة المرور الجديدة لـ «{users.find(u => u.id === resetId)?.fullName}»</label>
                    <input type="password" className="input w-64" required minLength={10}
                      value={resetValue} onChange={e => setResetValue(e.target.value)}
                      placeholder="10 محارف على الأقل، حرف ورقم" />
                  </div>
                  <button type="submit" className="btn-primary">حفظ</button>
                  <button type="button" className="btn-secondary" onClick={() => setResetId(null)}>إلغاء</button>
                </form>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
